import * as React from 'react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import { cn } from '@/_shared/lib/utils';

interface TruncateWithTooltipProps {
  children: string;
  className?: string;
  /** Make the text a tab stop while it is truncated, so the full value is reachable by keyboard. Opt-in. */
  focusable?: boolean;
}

/**
 * Whether the text is cut off. `scrollWidth` and `clientWidth` are whole pixels, so text that
 * overflows by a fraction of one — still drawn with an ellipsis — compares equal and was missed; the
 * text's own rendered width is compared as well, where the browser can measure it.
 */
function overflows(el: HTMLElement): boolean {
  if (el.scrollWidth > el.clientWidth) return true;
  const range = document.createRange();
  range.selectNodeContents(el);
  if (typeof range.getBoundingClientRect !== 'function') return false;
  return range.getBoundingClientRect().width > el.getBoundingClientRect().width;
}

/**
 * Single-line truncated text that shows the full string in a tooltip only when
 * overflow is detected (see `overflows`). Re-checks on resize.
 */
export function TruncateWithTooltip({
  children,
  className,
  focusable = false,
}: TruncateWithTooltipProps): React.ReactElement {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = React.useState(false);

  const checkTruncation = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setTruncated(overflows(el));
  }, []);

  React.useLayoutEffect(() => {
    checkTruncation();
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      checkTruncation();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation, children]);

  const text = (
    <span ref={ref} tabIndex={focusable && truncated ? 0 : undefined} className={cn('min-w-0 truncate', className)}>
      {children}
    </span>
  );

  if (!truncated) return text;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{text}</TooltipTrigger>
      <TooltipContent>{children}</TooltipContent>
    </Tooltip>
  );
}
